import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Clock } from "lucide-react";
import "../styles/ServiceFormAccessiblePickers.css";

type Props = {
  id: string;
  name: string;
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

function pad2(n: number) {
  return String(n).padStart(2, "0");
}

function parseTime(v: string): { h: number | null; m: number | null } {
  if (!v?.trim()) return { h: null, m: null };
  const parts = v.split(":");
  const hs = parts[0];
  const ms = parts[1];
  if (hs === undefined || ms === undefined) return { h: null, m: null };
  const h = Number.parseInt(hs, 10);
  const m = Number.parseInt(ms, 10);
  if (Number.isNaN(h) || Number.isNaN(m)) return { h: null, m: null };
  return {
    h: Math.min(23, Math.max(0, h)),
    m: Math.min(59, Math.max(0, m)),
  };
}

/** Enmascara dígitos a HH:MM mientras se escribe (p. ej. 1430 → 14:30). */
function maskTypedTime(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 4);
  if (digits.length === 0) return "";
  if (digits.length === 1) return digits;
  if (digits.length === 2) {
    return pad2(Math.min(23, Number.parseInt(digits, 10)));
  }
  const h = pad2(Math.min(23, Number.parseInt(digits.slice(0, 2), 10)));
  const minDigits = digits.slice(2);
  if (minDigits.length === 1) return `${h}:${minDigits}`;
  return `${h}:${pad2(Math.min(59, Number.parseInt(minDigits, 10)))}`;
}

/** Interpreta un borrador parcial (1, 14, 14:3, 14:30) para marcar/scroll. */
function draftToPartial(draft: string): { h: number | null; m: number | null } {
  const digits = draft.replace(/\D/g, "").slice(0, 4);
  if (digits.length === 0) return { h: null, m: null };

  let h: number | null = null;
  let m: number | null = null;

  if (digits.length === 1) {
    h = Number.parseInt(digits, 10);
  } else if (digits.length >= 2) {
    h = Math.min(23, Number.parseInt(digits.slice(0, 2), 10));
  }

  if (digits.length === 3) {
    // Tercer dígito → minuto 0–9 (luego el 4.º completa 00–59).
    m = Number.parseInt(digits.slice(2), 10);
  } else if (digits.length >= 4) {
    m = Math.min(59, Number.parseInt(digits.slice(2, 4), 10));
  }

  return { h, m };
}

function scrollOptIntoCenter(
  col: HTMLElement | null,
  opt: HTMLElement | null,
  behavior: ScrollBehavior
) {
  if (!col || !opt) return;
  const top = opt.offsetTop - col.clientHeight / 2 + opt.offsetHeight / 2;
  col.scrollTo({ top: Math.max(0, top), behavior });
}

const HOURS = Array.from({ length: 24 }, (_, i) => i);
const MINUTES = Array.from({ length: 60 }, (_, i) => i);

const AccessibleTimePicker: React.FC<Props> = ({
  id,
  name,
  label,
  value,
  onChange,
}) => {
  const { h: vh, m: vm } = parseTime(value);
  const [open, setOpen] = useState(false);
  const [popoverStyle, setPopoverStyle] = useState<React.CSSProperties>({});
  const [typedDraft, setTypedDraft] = useState("");
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hourColRef = useRef<HTMLDivElement>(null);
  const minColRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const digitInputRef = useRef<HTMLInputElement>(null);
  const scrollBehaviorRef = useRef<ScrollBehavior>("auto");
  const skipValueSyncRef = useRef(false);

  const fire = useCallback(
    (v: string) => {
      onChange({
        target: { name, value: v },
        currentTarget: { name, value: v },
      } as React.ChangeEvent<HTMLInputElement>);
    },
    [name, onChange]
  );

  const setPair = useCallback(
    (h: number, m: number) => {
      const next = `${pad2(h)}:${pad2(m)}`;
      skipValueSyncRef.current = true;
      setTypedDraft(next);
      fire(next);
    },
    [fire]
  );

  const scrollToSelection = useCallback(
    (h: number | null, m: number | null, behavior: ScrollBehavior) => {
      const preferSmooth =
        typeof window !== "undefined" &&
        !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      const scrollBehavior: ScrollBehavior =
        behavior === "smooth" && preferSmooth ? "smooth" : "auto";

      const selH =
        h !== null
          ? hourColRef.current?.querySelector<HTMLElement>(`[data-hour="${h}"]`)
          : null;
      const selM =
        m !== null
          ? minColRef.current?.querySelector<HTMLElement>(
              `[data-minute="${m}"]`
            )
          : null;
      scrollOptIntoCenter(hourColRef.current, selH ?? null, scrollBehavior);
      scrollOptIntoCenter(minColRef.current, selM ?? null, scrollBehavior);
    },
    []
  );

  const updatePopoverPosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const vw = window.innerWidth;
    const viewH = window.innerHeight;
    const popW = Math.min(320, vw - 24);
    let left = r.left + r.width / 2 - popW / 2;
    left = Math.max(12, Math.min(left, vw - popW - 12));
    const below = r.bottom + 8;
    const popH = 380;
    const openUp = below + popH > viewH - 12 && r.top > popH + 16;
    const top = openUp ? r.top - popH - 8 : below;
    setPopoverStyle({
      position: "fixed",
      left,
      top: Math.max(12, Math.min(top, viewH - popH - 12)),
      width: popW,
      zIndex: 10060,
    });
  }, []);

  useLayoutEffect(() => {
    if (!open) return;
    updatePopoverPosition();
    const ro = () => updatePopoverPosition();
    window.addEventListener("resize", ro);
    window.addEventListener("scroll", ro, true);
    return () => {
      window.removeEventListener("resize", ro);
      window.removeEventListener("scroll", ro, true);
    };
  }, [open, updatePopoverPosition]);

  useEffect(() => {
    if (!open) return;
    const initial =
      vh !== null && vm !== null ? `${pad2(vh)}:${pad2(vm)}` : "";
    setTypedDraft(initial);
    scrollBehaviorRef.current = "auto";
    const t = window.setTimeout(() => {
      scrollToSelection(vh, vm, "auto");
      digitInputRef.current?.focus();
      digitInputRef.current?.select();
    }, 0);
    return () => window.clearTimeout(t);
    // Solo al abrir.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (skipValueSyncRef.current) {
      skipValueSyncRef.current = false;
      return;
    }
    if (vh !== null && vm !== null) {
      const next = `${pad2(vh)}:${pad2(vm)}`;
      setTypedDraft((prev) => (prev === next ? prev : next));
    } else if (!value) {
      setTypedDraft("");
    }
  }, [open, value, vh, vm]);

  const { h: draftH, m: draftM } = useMemo(
    () => draftToPartial(typedDraft),
    [typedDraft]
  );
  const highlightH = draftH ?? vh;
  const highlightM = draftM ?? vm;

  useEffect(() => {
    if (!open) return;
    const behavior = scrollBehaviorRef.current;
    const t = window.setTimeout(() => {
      scrollToSelection(highlightH, highlightM, behavior);
      scrollBehaviorRef.current = "auto";
    }, 0);
    return () => window.clearTimeout(t);
  }, [open, highlightH, highlightM, scrollToSelection]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (triggerRef.current?.contains(t)) return;
      if (popoverRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const applyTypedDraft = useCallback(
    (draft: string) => {
      const masked = maskTypedTime(draft);
      scrollBehaviorRef.current = "smooth";
      setTypedDraft(masked);

      const digits = masked.replace(/\D/g, "");
      if (!digits) {
        skipValueSyncRef.current = true;
        fire("");
        return;
      }

      const { h, m } = draftToPartial(masked);
      // Confirma valor del formulario solo con HH:MM completo.
      if (digits.length >= 4 && h !== null && m !== null) {
        skipValueSyncRef.current = true;
        fire(`${pad2(h)}:${pad2(m)}`);
      }
    },
    [fire]
  );

  const commitDraftOrClose = useCallback(() => {
    const { h, m } = draftToPartial(typedDraft);
    const digits = typedDraft.replace(/\D/g, "");
    if (digits.length >= 4 && h !== null && m !== null) {
      skipValueSyncRef.current = true;
      fire(`${pad2(h)}:${pad2(m)}`);
    } else if (h !== null) {
      skipValueSyncRef.current = true;
      const next = `${pad2(h)}:${pad2(m ?? vm ?? 0)}`;
      setTypedDraft(next);
      fire(next);
    }
    setOpen(false);
  }, [typedDraft, fire, vm]);

  const displayLabel = useMemo(() => {
    if (vh === null || vm === null) return null;
    return `${pad2(vh)}:${pad2(vm)}`;
  }, [vh, vm]);

  const popoverId = `${id}-time-popover`;
  const digitInputId = `${id}-digit`;

  const portal =
    open &&
    typeof document !== "undefined" &&
    createPortal(
      <>
        <div
          className="a11y-time__backdrop"
          aria-hidden
          onClick={() => setOpen(false)}
        />
        <div
          ref={popoverRef}
          id={popoverId}
          role="dialog"
          aria-modal="true"
          aria-label="Elegir hora del servicio"
          className="a11y-time__popover"
          style={popoverStyle}
        >
          <div className="a11y-time__popover-head">
            <span className="a11y-time__popover-title">Hora</span>
            <span className="a11y-time__popover-title">Min</span>
          </div>
          <div className="a11y-time__popover-cols">
            <div ref={hourColRef} className="a11y-time__col" role="listbox">
              {HOURS.map((h) => (
                <button
                  key={h}
                  type="button"
                  role="option"
                  data-hour={h}
                  aria-selected={highlightH === h}
                  className={`a11y-time__opt${highlightH === h ? " a11y-time__opt--active" : ""}`}
                  onClick={() => {
                    scrollBehaviorRef.current = "smooth";
                    setPair(h, vm ?? 0);
                  }}
                >
                  {pad2(h)}
                </button>
              ))}
            </div>
            <div ref={minColRef} className="a11y-time__col" role="listbox">
              {MINUTES.map((m) => (
                <button
                  key={m}
                  type="button"
                  role="option"
                  data-minute={m}
                  aria-selected={highlightM === m}
                  className={`a11y-time__opt${highlightM === m ? " a11y-time__opt--active" : ""}`}
                  onClick={() => {
                    scrollBehaviorRef.current = "smooth";
                    setPair(vh ?? 0, m);
                  }}
                >
                  {pad2(m)}
                </button>
              ))}
            </div>
          </div>
          <div className="a11y-time__digit-row">
            <label className="a11y-time__digit-label" htmlFor={digitInputId}>
              Escribir hora
            </label>
            <input
              ref={digitInputRef}
              id={digitInputId}
              className="a11y-time__digit-input"
              type="text"
              inputMode="numeric"
              autoComplete="off"
              placeholder="HH : MM"
              maxLength={5}
              value={typedDraft}
              aria-describedby={`${id}-digit-hint`}
              onChange={(e) => applyTypedDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  commitDraftOrClose();
                }
              }}
            />
            <span id={`${id}-digit-hint`} className="a11y-sr-only">
              Escribe cuatro dígitos, por ejemplo 1430 para las 14:30. Las
              listas se desplazan al escribir.
            </span>
          </div>
          <div className="a11y-time__popover-foot">
            <button
              type="button"
              className="a11y-time__done"
              onClick={commitDraftOrClose}
            >
              Listo
            </button>
          </div>
        </div>
      </>,
      document.body
    );

  return (
    <div className="a11y-time">
      <div className="a11y-time__label" id={`${id}-label`}>
        {label}
      </div>
      <div
        className="a11y-time__shell a11y-time__shell--modern"
        role="group"
        aria-labelledby={`${id}-label`}
      >
        <button
          ref={triggerRef}
          type="button"
          id={id}
          className="a11y-time__trigger"
          aria-expanded={open}
          aria-haspopup="dialog"
          aria-controls={open ? popoverId : undefined}
          onClick={() => setOpen((o) => !o)}
        >
          <span
            className={`a11y-time__trigger-value${displayLabel ? "" : " a11y-time__trigger-value--placeholder"}`}
          >
            {displayLabel ?? "HH : MM"}
          </span>
          <ChevronDown
            size={22}
            className={`a11y-time__trigger-chevron${open ? " a11y-time__trigger-chevron--open" : ""}`}
            aria-hidden
          />
        </button>
        <div className="a11y-time__icon" aria-hidden>
          <Clock size={28} strokeWidth={2} />
        </div>
      </div>
      <span id={`${id}-hint`} className="a11y-sr-only">
        Formato 24 horas. Abre el panel para elegir o escribir la hora.
      </span>
      {portal}
    </div>
  );
};

export default AccessibleTimePicker;
